/**
 * React Node View für PaperlessDocLink.
 *
 * Lese-Modus: Kompakte einzeilige Inline-Referenz (Icon + Titel).
 * Edit-Modus: Block-Karte mit Cover-Thumbnail, Drag-Handle, Edit/Delete.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { NodeViewWrapper } from '@tiptap/react'
import { NodeViewProps } from '@tiptap/core'
import { EMBED_DRAG_DATA } from './EmbedDragPlugin'
import { getDocumentThumbnailUrl } from '../paperless/api'

const DOC_COLOR = '#3b82f6'

export function PaperlessDocLinkView({ node, extension, getPos, editor, updateAttributes }: NodeViewProps) {
  const { docId, title, fileName } = node.attrs as { docId: number; title: string; fileName: string }
  const [isHovered, setIsHovered] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(title)
  const [docMeta, setDocMeta] = useState<any>(null)
  const [thumbError, setThumbError] = useState(false)
  const isEditable = editor?.isEditable ?? false

  useEffect(() => {
    function resolve() {
      const docs: any[] = (window as any)._paperlessDocs || []
      const found = docs.find((d: any) => d.id === docId)
      if (found) setDocMeta(found)
    }
    resolve()
    const timer = setInterval(resolve, 3000)
    return () => clearInterval(timer)
  }, [docId])

  function handleClick() {
    if (isEditing) return
    const cb = extension.options.onDocClick
    if (cb) cb(docId, title)
  }

  function handleDragStart(e: React.DragEvent) {
    const pos = getPos()
    if (typeof pos !== 'number') return
    e.dataTransfer.setData(EMBED_DRAG_DATA, JSON.stringify({ pos }))
    e.dataTransfer.effectAllowed = 'move'
    const card = (e.target as HTMLElement).closest('.embed-card')
    if (card) e.dataTransfer.setDragImage(card as HTMLElement, 20, 20)
  }

  const handleDelete = useCallback(() => {
    const pos = getPos()
    if (typeof pos !== 'number') return
    editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run()
  }, [editor, getPos, node.nodeSize])

  const handleEditSave = useCallback(() => {
    updateAttributes({ title: editTitle })
    setIsEditing(false)
  }, [editTitle, updateAttributes])

  function formatDate(iso: string): string {
    try {
      return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    } catch { return iso }
  }

  const displayTitle = docMeta?.title || title || 'Dokument'
  const displayFile  = docMeta?.original_file_name || fileName || ''
  const pageCount    = docMeta?.page_count ?? null
  const created      = docMeta?.created ?? null
  const docType      = docMeta?.document_type_name ?? docMeta?.docTypeName ?? null
  const tags: string[] = docMeta?.tags_display || docMeta?.tags || []
  const correspondent = docMeta?.correspondent_name ?? docMeta?.correspondent__name ?? null
  const thumbUrl = getDocumentThumbnailUrl(docId)

  // ── Lese-Modus: Card mit Cover, Titel, Autor, Tags ───────────────────────
  if (!isEditable) {
    return (
      <NodeViewWrapper data-type="paperless-doc-link" data-doc-id={docId}>
        <div
          className="embed-doc-read"
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            background: isHovered ? `${DOC_COLOR}0a` : 'hsl(220 14% 96%)',
          }}
        >
          {/* Cover */}
          {!thumbError ? (
            <div className="embed-doc-read__cover">
              <img
                src={thumbUrl}
                alt=""
                onError={() => setThumbError(true)}
              />
            </div>
          ) : (
            <div className="embed-doc-read__cover embed-doc-read__cover--fallback">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={DOC_COLOR} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
          )}

          {/* Text-Block */}
          <div className="embed-doc-read__body">
            <div className="embed-doc-read__title">{displayTitle}</div>
            {correspondent && (
              <div className="embed-doc-read__author">{correspondent}</div>
            )}
            {tags.length > 0 && (
              <div className="embed-doc-read__tags">
                {(typeof tags[0] === 'string' ? tags : []).map((t: string, i: number) => (
                  <span key={i} className="embed-doc-read__tag">{t}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </NodeViewWrapper>
    )
  }

  // ── Edit-Modus: volle Block-Karte ─────────────────────────────────────────
  return (
    <NodeViewWrapper data-type="paperless-doc-link" data-doc-id={docId}>
      <div
        className="embed-card"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          background: isHovered ? `${DOC_COLOR}0d` : `${DOC_COLOR}08`,
          borderTopColor: isHovered ? `${DOC_COLOR}55` : `${DOC_COLOR}33`,
          borderRightColor: isHovered ? `${DOC_COLOR}55` : `${DOC_COLOR}33`,
          borderBottomColor: isHovered ? `${DOC_COLOR}55` : `${DOC_COLOR}33`,
          borderLeftColor: DOC_COLOR,
          borderLeftWidth: '3px',
        }}
      >
        <div className="embed-card__grip" draggable="true" onDragStart={handleDragStart}>
          <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" opacity="0.35">
            <circle cx="3" cy="2" r="1.2"/><circle cx="7" cy="2" r="1.2"/>
            <circle cx="3" cy="6" r="1.2"/><circle cx="7" cy="6" r="1.2"/>
            <circle cx="3" cy="10" r="1.2"/><circle cx="7" cy="10" r="1.2"/>
            <circle cx="3" cy="14" r="1.2"/><circle cx="7" cy="14" r="1.2"/>
          </svg>
        </div>

        {!thumbError && (
          <div className="embed-card__thumb" onClick={handleClick}>
            <img
              src={thumbUrl}
              alt={displayTitle}
              onError={() => setThumbError(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}

        <div className="embed-card__body" onClick={handleClick}>
          <div className="embed-card__header">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={DOC_COLOR} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            {isEditing ? (
              <input
                className="embed-card__edit-input"
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                onBlur={handleEditSave}
                onKeyDown={e => e.key === 'Enter' && handleEditSave()}
                autoFocus
                onClick={e => e.stopPropagation()}
              />
            ) : (
              <span className="embed-card__title" style={{ color: DOC_COLOR }}>{displayTitle}</span>
            )}
            {docType && (
              <span className="embed-card__badge" style={{ background: `${DOC_COLOR}20`, color: DOC_COLOR }}>{docType}</span>
            )}
          </div>

          <div className="embed-card__meta">
            {displayFile && (
              <span className="embed-card__meta-item embed-card__meta-dim" style={{ fontFamily: 'var(--font-mono, monospace)' }}>
                {displayFile}
              </span>
            )}
            {pageCount != null && (
              <span className="embed-card__meta-item">
                {pageCount} Seite{pageCount !== 1 ? 'n' : ''}
              </span>
            )}
            {created && (
              <span className="embed-card__meta-item embed-card__meta-dim">
                {formatDate(created)}
              </span>
            )}
            <span className="embed-card__meta-item embed-card__meta-dim">
              ID: {docId}
            </span>
            {tags.length > 0 && (
              <span className="embed-card__meta-item embed-card__meta-tags">
                {(typeof tags[0] === 'string' ? tags : []).map((t: string, i: number) => (
                  <span key={i} className="embed-card__tag">{t}</span>
                ))}
              </span>
            )}
          </div>
        </div>

        {isEditable && isHovered && (
          <div className="embed-card__actions" onClick={e => e.stopPropagation()}>
            <button className="embed-card__action-btn" title="Bearbeiten" onClick={() => { setEditTitle(title); setIsEditing(!isEditing) }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
            <button className="embed-card__action-btn embed-card__action-btn--danger" title="Entfernen" onClick={handleDelete}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
          </div>
        )}
      </div>
    </NodeViewWrapper>
  )
}
